import {
  CoreStockType,
  OrderStatus,
  StockType,
} from '@kinet-open-network/shared/data-access/model';
import { FlowOrderType } from './flow-order.interface';

/** Order fields holding the saved selection items of a single flow step. */
export type FlowOrderStepItemsKey =
  | 'toCustomerLoadedItems'
  | 'toCustomerUnloadedItems'
  | 'fromCustomerLoadedItems'
  | 'fromCustomerUnloadedItems';

interface IStepItem {
  id: string;
  scanItems: unknown[];
  manualCount: number;
  data?: { isService?: boolean } | null;
}

/** Subset of an order (entity or client model) the step definitions work with. */
export interface IFlowOrderStepSource
  extends Partial<Record<FlowOrderStepItemsKey, IStepItem[] | null>> {
  status: OrderStatus;
  type?: FlowOrderType | null;
  customerId?: number | string | null;
  stockCode?: string | null;
  unloadingStockCode?: string | null;
  customer?: {
    isStock?: boolean | null;
    stockCode?: string[] | null;
  } | null;
  items?:
    | {
        id: string;
        quantity: number;
        stockItem?: { isService?: boolean | null } | null;
      }[]
    | null;
}

export interface IFlowOrderStepStockPoint {
  id?: string | null;
  type: StockType;
}

export interface IFlowOrderStepStockMove {
  from: IFlowOrderStepStockPoint;
  to: IFlowOrderStepStockPoint;
  /** Services have no physical units, so they never move. */
  skipServices?: boolean;
}

export interface IFlowOrderStepItemsDefinition {
  key: FlowOrderStepItemsKey;
  label: string;
  /** Whether the selection should default to scanning full bottles. */
  defaultFullScan: boolean;
  /** Whether service items are resolved from the order's own items on save. */
  appliesServiceItemDefaults(order: IFlowOrderStepSource): boolean;
  /** Amounts per item id the step's selection is expected to reach (shown as "count / required"). */
  getRequiredAmounts(
    order: IFlowOrderStepSource,
  ): { id: string; amount: number }[];
  /** Whether the step can only be saved once the required amounts are reached. */
  enforceRequiredAmounts: boolean;
  /** Stock move the step's items produce; `deliveryModeId` comes from the order's delivery. */
  getStockMove(
    order: IFlowOrderStepSource,
    deliveryModeId?: string | null,
  ): IFlowOrderStepStockMove;
}

const statuses = Object.values(OrderStatus);

const hasPassed = (order: IFlowOrderStepSource, status: OrderStatus) =>
  statuses.indexOf(order.status) > statuses.indexOf(status);

const isStoreSale = (order: IFlowOrderStepSource) =>
  order.type === FlowOrderType.storeSale;

const isDeliveryOrder = (order: IFlowOrderStepSource) =>
  !order.type || order.type === FlowOrderType.order;

const isStockTransfer = (order: IFlowOrderStepSource) =>
  !!order.customer?.isStock;

const totalCount = (item: IStepItem) =>
  item.scanItems.length + item.manualCount;

const stockPoint = (
  id: string | number | null | undefined,
  type: StockType,
): IFlowOrderStepStockPoint => ({ id: id?.toString(), type });

/** Ordered items the customer is to receive (services are resolved by the server). */
const orderedAmounts = (order: IFlowOrderStepSource) =>
  (order.items ?? [])
    .filter((item) => !item.stockItem?.isService)
    .map((item) => ({ id: item.id, amount: item.quantity }));

/**
 * Single source of truth for the item lists of an order's steps: what they
 * are expected to reach and which stock move they produce. Adding a list means
 * adding its key to `FlowOrderStepItemsKey` and an entry here (and to the
 * status step that handles it in `flowOrderStatusSteps`).
 */
export const flowOrderStepItems: Record<
  FlowOrderStepItemsKey,
  IFlowOrderStepItemsDefinition
> = {
  // The ordered amounts are shown, but anything may be loaded on the car and
  // unloaded at the customer — different amounts can be saved too.
  toCustomerLoadedItems: {
    key: 'toCustomerLoadedItems',
    label: 'Fľaše naložené zo skladu',
    defaultFullScan: true,
    appliesServiceItemDefaults: () => true,
    getRequiredAmounts: orderedAmounts,
    enforceRequiredAmounts: false,
    getStockMove: (order, deliveryModeId) => ({
      from: stockPoint(order.stockCode, CoreStockType.stock),
      to: stockPoint(deliveryModeId, CoreStockType.deliveryMode),
      skipServices: true,
    }),
  },
  toCustomerUnloadedItems: {
    key: 'toCustomerUnloadedItems',
    label: 'Fľaše vyložené u zákazníka',
    defaultFullScan: true,
    appliesServiceItemDefaults: (order) => !isStoreSale(order),
    getRequiredAmounts: (order) =>
      isStoreSale(order) ? [] : orderedAmounts(order),
    enforceRequiredAmounts: false,
    getStockMove: (order, deliveryModeId) => ({
      from: isStoreSale(order)
        ? stockPoint(order.stockCode, CoreStockType.stock)
        : stockPoint(deliveryModeId, CoreStockType.deliveryMode),
      to: stockPoint(order.customerId, CoreStockType.customer),
      skipServices: true,
    }),
  },
  fromCustomerLoadedItems: {
    key: 'fromCustomerLoadedItems',
    label: 'Fľaše prijaté od zákazníka',
    defaultFullScan: false,
    appliesServiceItemDefaults: () => false,
    getRequiredAmounts: () => [],
    enforceRequiredAmounts: true,
    getStockMove: (order, deliveryModeId) => ({
      from: stockPoint(order.customerId, CoreStockType.customer),
      to: stockPoint(deliveryModeId, CoreStockType.deliveryMode),
    }),
  },
  fromCustomerUnloadedItems: {
    key: 'fromCustomerUnloadedItems',
    label: 'Fľaše vyložené na sklade',
    defaultFullScan: false,
    appliesServiceItemDefaults: () => false,
    // The stock has to receive everything that was loaded before.
    enforceRequiredAmounts: true,
    getRequiredAmounts: (order) =>
      isStoreSale(order)
        ? []
        : (
            (isStockTransfer(order)
              ? order.toCustomerLoadedItems
              : order.fromCustomerLoadedItems) ?? []
          )
            .filter((item) => !item.data?.isService)
            .map((item) => ({ id: item.id, amount: totalCount(item) })),
    getStockMove: (order, deliveryModeId) => ({
      from: isStoreSale(order)
        ? stockPoint(order.customerId, CoreStockType.customer)
        : stockPoint(deliveryModeId, CoreStockType.deliveryMode),
      to: stockPoint(
        isStoreSale(order)
          ? order.stockCode
          : (order.unloadingStockCode ??
              (isStockTransfer(order)
                ? order.customer?.stockCode?.[0]
                : order.stockCode)),
        CoreStockType.stock,
      ),
    }),
  },
};

/**
 * All item lists in processing order — a list may depend on the items of the
 * lists before it (see `getRequiredAmounts`).
 */
export const flowOrderStepItemsKeys = Object.keys(
  flowOrderStepItems,
) as FlowOrderStepItemsKey[];

/**
 * A step of the order flow, identified by the status the order is in while the
 * step is performed. The same step is submitted when it is performed for the
 * first time and when it is edited retroactively, always with this `status`.
 */
export interface IFlowOrderStatusStepDefinition {
  status: OrderStatus;
  label: string;
  /** Item lists handled by the step, in processing order. */
  getItemKeys(order: IFlowOrderStepSource): FlowOrderStepItemsKey[];
  /**
   * Whether the order already went through the step, i.e. it can be edited.
   * Mirrors the status transitions of `OrderFlowHandler.getNextStepStatus`.
   */
  isCompleted(order: IFlowOrderStepSource): boolean;
  /** Whether the order's invoice is generated from the step's items. */
  generatesInvoice: boolean;
  /**
   * Non-item step data persisted on the order. It is required when the step is
   * performed and carried over unchanged when the step is edited.
   */
  savedDataKeys: string[];
}

const recipientDataKeys = [
  'recipientName',
  'recipientEmail',
  'recipientSignature',
  'deliveryDate',
  'recipientGpsX',
  'recipientGpsY',
];

export const flowOrderStatusSteps: Partial<
  Record<OrderStatus, IFlowOrderStatusStepDefinition>
> = {
  // Only the store sale performs its items in the "approved" status.
  [OrderStatus.approved]: {
    status: OrderStatus.approved,
    label: 'Predaj',
    getItemKeys: (order) =>
      isStoreSale(order)
        ? ['fromCustomerUnloadedItems', 'toCustomerUnloadedItems']
        : [],
    isCompleted: (order) =>
      isStoreSale(order) && order.status === OrderStatus.finished,
    generatesInvoice: true,
    savedDataKeys: recipientDataKeys,
  },
  [OrderStatus.planned]: {
    status: OrderStatus.planned,
    label: 'Naloženie auta',
    getItemKeys: () => ['toCustomerLoadedItems'],
    isCompleted: (order) =>
      isDeliveryOrder(order) && hasPassed(order, OrderStatus.planned),
    generatesInvoice: false,
    savedDataKeys: [],
  },
  [OrderStatus.delivery]: {
    status: OrderStatus.delivery,
    label: 'Doručenie',
    getItemKeys: () => ['toCustomerUnloadedItems', 'fromCustomerLoadedItems'],
    isCompleted: (order) =>
      isDeliveryOrder(order) &&
      !isStockTransfer(order) &&
      hasPassed(order, OrderStatus.delivery),
    generatesInvoice: true,
    savedDataKeys: recipientDataKeys,
  },
  // Collection orders skip planning and delivery and load from the customer here.
  [OrderStatus.delivered]: {
    status: OrderStatus.delivered,
    label: 'Zvoz od zákazníka',
    getItemKeys: () => ['fromCustomerLoadedItems'],
    isCompleted: (order) =>
      order.type === FlowOrderType.collection &&
      hasPassed(order, OrderStatus.delivered),
    generatesInvoice: true,
    savedDataKeys: recipientDataKeys,
  },
  [OrderStatus.collection]: {
    status: OrderStatus.collection,
    label: 'Vyloženie na sklade',
    getItemKeys: () => ['fromCustomerUnloadedItems'],
    // The unloading stock is only saved once the step was actually performed.
    isCompleted: (order) =>
      !isStoreSale(order) &&
      order.status === OrderStatus.finished &&
      !!order.unloadingStockCode,
    generatesInvoice: false,
    savedDataKeys: ['unloadingStockCode'],
  },
};

/** Steps the order already went through, in flow order. */
export function getEditableOrderSteps(
  order: IFlowOrderStepSource,
): IFlowOrderStatusStepDefinition[] {
  return statuses
    .map((status) => flowOrderStatusSteps[status])
    .filter(
      (step): step is IFlowOrderStatusStepDefinition =>
        !!step && step.isCompleted(order) && !!step.getItemKeys(order).length,
    );
}
